/**
 * QuickActionsRow — Two-button horizontal strip below the stat grid.
 *
 * "Invite Client" uses GlassButton (theatrical gradient, compact size, iconLeft).
 * "Share Link" uses a one-off outline Pressable — not a new GlassButton variant.
 *
 * Both buttons must meet 44px minimum height (WCAG 2.5.5).
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';
import { GlassButton } from '../../glass/GlassButton';

const INVITE_ICON = <Ionicons name="person-add-outline" size={16} color="#fff" />;
const SHARE_ICON = <Ionicons name="link-outline" size={16} color="#7C3AED" />;

interface QuickActionsRowProps {
  onInvitePress: () => void;
  onSharePress: () => void;
  isInviteLoading?: boolean;
  t: TFunction<'practitioner'>;
}

export const QuickActionsRow: React.FC<QuickActionsRowProps> = ({
  onInvitePress,
  onSharePress,
  isInviteLoading = false,
  t,
}) => {
  return (
    <View style={styles.container}>
      <GlassButton
        variant="theatrical"
        size="compact"
        iconLeft={INVITE_ICON}
        style={styles.inviteButton}
        onPress={onInvitePress}
        disabled={isInviteLoading}
        accessibilityLabel={t('actions.invite_client')}
      >
        {t('actions.invite_client')}
      </GlassButton>

      <Pressable
        style={({ pressed }) => [styles.outlineBtn, pressed && styles.outlineBtnPressed]}
        onPress={onSharePress}
        accessibilityRole="button"
        accessibilityLabel={t('actions.share_link')}
      >
        {SHARE_ICON}
        <Text style={styles.outlineBtnText}>{t('actions.share_link')}</Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  inviteButton: {
    flex: 1,
    borderRadius: 12,
    minWidth: 0,
  },
  outlineBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 44,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#A78BFA',
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  outlineBtnPressed: {
    opacity: 0.75,
  },
  outlineBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#7C3AED',
  },
});
